/**
 * SMS counting for the editors: how a text will be encoded and how many
 * segments the carrier will bill.
 *
 * The server counts the same thing at enqueue (`internal/sms`) and is the
 * authority — a template preview returns its `sms` — but the compose form and
 * the template editor need the number as you type, so it is mirrored here.
 * The tables and boundaries must stay in step with `internal/sms/segments.go`.
 */

/** GSM 03.38 basic character set: one septet each. */
const GSM7_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§" +
  "¿abcdefghijklmnopqrstuvwxyzäöñüà";

/** These cost two septets: an escape plus the character. */
const GSM7_EXTENDED = "\f^{}\\[~]|€";

const GSM7_SINGLE = 160;
const GSM7_MULTI = 153;
const UCS2_SINGLE = 70;
const UCS2_MULTI = 67;

export interface SmsInfo {
  encoding: "gsm7" | "ucs2";
  /** What the carrier bills. */
  segments: number;
  /** Payload units: septets for GSM-7, UTF-16 code units for UCS-2. */
  units: number;
  /** Characters — what a person counts. */
  length: number;
  /** The first character that forced UCS-2, or null when the text is GSM-7. */
  firstNonGsm: string | null;
}

/**
 * Counts what a text will cost. One character outside GSM 03.38 moves the
 * whole message to UCS-2 and more than halves what fits in a segment, so the
 * character responsible is reported — "é is fine but ě is not" is otherwise
 * baffling.
 */
export function smsSegments(text: string): SmsInfo {
  const chars = [...text];
  let encoding: SmsInfo["encoding"] = "gsm7";
  let firstNonGsm: string | null = null;
  let widths: number[] = [];

  for (const ch of chars) {
    if (GSM7_BASIC.includes(ch)) {
      widths.push(1);
    } else if (GSM7_EXTENDED.includes(ch)) {
      widths.push(2);
    } else {
      if (firstNonGsm === null) firstNonGsm = ch;
      encoding = "ucs2";
    }
  }

  if (encoding === "ucs2") {
    // In UCS-2 every character costs its UTF-16 code units, so the GSM widths
    // collected above no longer apply.
    widths = chars.map((ch) => (ch.codePointAt(0)! > 0xffff ? 2 : 1));
  }

  const single = encoding === "gsm7" ? GSM7_SINGLE : UCS2_SINGLE;
  const multi = encoding === "gsm7" ? GSM7_MULTI : UCS2_MULTI;
  const units = widths.reduce((sum, w) => sum + w, 0);

  return {
    encoding,
    segments: countSegments(widths, units, single, multi),
    units,
    length: chars.length,
    firstNonGsm,
  };
}

/**
 * Packs the widths into segments without ever splitting a two-unit character
 * across a boundary: it moves whole, leaving a unit of a segment unused.
 */
function countSegments(widths: number[], total: number, single: number, multi: number): number {
  if (total === 0 || total <= single) return 1;
  let n = 1;
  let used = 0;
  for (const w of widths) {
    if (used + w > multi) {
      n++;
      used = 0;
    }
    used += w;
  }
  return n;
}

/** A one-line summary for a form hint: "94 characters · 1 segment". */
export function smsHint(text: string): string {
  const info = smsSegments(text);
  const parts = [
    `${info.length} character${info.length === 1 ? "" : "s"}`,
    `${info.segments} segment${info.segments === 1 ? "" : "s"}`,
  ];
  if (info.encoding === "ucs2" && info.firstNonGsm) {
    parts.push(`UCS-2 because of “${info.firstNonGsm}”`);
  }
  return parts.join(" · ");
}

/**
 * Drops a parenthesised trunk code — the "(0)" in "+44 (0) 20 7946 0958",
 * dialled instead of the country code domestically — while keeping the digits
 * of a parenthesised area code like "(555)". Returns null for an unbalanced
 * bracket, which is refused rather than guessed at.
 *
 * Must match `stripParens` in internal/sms/e164.go.
 */
/** The set Go's strings.TrimSpace removes, and only that set. */
function trimGoSpace(value: string): string {
  return value.replace(/^[ \t\n\v\f\r\u0085\u00a0]+|[ \t\n\v\f\r\u0085\u00a0]+$/g, "");
}

function stripParens(raw: string): string | null {
  if (!raw.includes("(") && !raw.includes(")")) return raw;
  let out = "";
  let i = 0;
  while (i < raw.length) {
    if (raw[i] !== "(") {
      if (raw[i] === ")") return null;
      out += raw[i];
      i++;
      continue;
    }
    const close = raw.indexOf(")", i);
    if (close < 0) return null;
    // Go's strings.TrimSpace, not JavaScript's trim(): the same reason
    // SEPARATORS below exists. JS strips the BOM and a handful of spaces Go
    // does not, so trimming here would call a number valid that the server
    // then refuses — and, in the other direction, refuse one it accepts.
    // Leading zeros inside the brackets are the trunk prefix ("(0)" alone, or
    // the zero in "(020)"); an area code in E.164 form never starts with one,
    // and keeping it turns +44 (020) 7946 0958 into a different valid-looking
    // number. Mirrors stripParens in internal/sms/e164.go.
    const inner = trimGoSpace(raw.slice(i + 1, close)).replace(/^0+/, "");
    out += inner;
    i = close + 1;
  }
  return out;
}

/**
 * The separators the server strips. Deliberately NOT JavaScript's `\s`, which
 * also covers em/thin/ideographic spaces and the BOM that Go's set does not —
 * accepting those here would show a valid field and then take a 422 from the
 * server. Must match `separators` in internal/sms/e164.go.
 */
const SEPARATORS = /[ \t\r\n\-./   ]/g;

/**
 * Whether a number is in the E.164 form letterman stores. Spaces, dashes and
 * a leading 00 are accepted and normalized by the server; a number with no
 * "+" or "00" is refused rather than given a guessed country.
 */
export function isPhoneNumber(raw: string): boolean {
  const unbracketed = stripParens(raw);
  if (unbracketed === null) return false;
  const cleaned = unbracketed.replace(SEPARATORS, "");
  const digits = cleaned.startsWith("+")
    ? cleaned.slice(1)
    : cleaned.startsWith("00")
      ? cleaned.slice(2)
      : null;
  if (digits === null) return false;
  return /^[1-9][0-9]{6,14}$/.test(digits);
}
